import { PortalReadState } from '../portal-read-state.tsx';
import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { partnerApi } from '../../api/partner.ts';
import { usePartnerIdentity } from '../../components/partner-layout.tsx';
import { Alert, Button, LoadingBlock } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { money, periodLabel, Stat } from './common.tsx';

/**
 * Where a signed-in partner lands.
 *
 * Deliberately this screen and not the security one. A partner signs in to see
 * what they have earned; being met instead by an enrollment form — with no
 * other page reachable — reads as "this portal requires two-factor
 * authentication" even on a deployment where it is entirely optional, which is
 * exactly what it used to say by accident.
 */
export default function PartnerOverviewPage() {
  useDocumentTitle('Partner overview');
  const identity = usePartnerIdentity();
  const [copied, setCopied] = useState<'yes' | 'unavailable' | null>(null);

  const dashboard = useQuery({
    queryKey: ['partner', 'dashboard'],
    queryFn: partnerApi.dashboard,
  });

  if (dashboard.isLoading) return <PortalReadState title="Partner overview" label="Loading your figures…" />;
  if (dashboard.isError || !dashboard.data) {
    return (
      <PortalReadState
        title="Partner overview"
        error={dashboard.error instanceof Error ? dashboard.error.message : 'Your figures could not be loaded.'}
        retry={() => dashboard.refetch()}
      />
    );
  }

  const { partner, tier, customers, earnings } = dashboard.data;
  const mfa = identity.data?.mfa;
  // Every figure on this screen is an aggregate, so it carries the ledger's
  // own currency rather than falling back to the formatter's USD default.
  const cash = (cents: number) => money(cents, earnings.currency);

  async function copyLink() {
    // `navigator.clipboard?.writeText(...)` short-circuits to undefined where
    // there is no clipboard — it never throws, so the catch never ran and the
    // page said "Copied." having copied nothing. An insecure origin and a
    // private window both land there, and both deserve to be told rather than
    // left with a button that silently does nothing.
    if (!navigator.clipboard) return setCopied('unavailable');
    try {
      await navigator.clipboard.writeText(partner.url);
      setCopied('yes');
    } catch {
      setCopied('unavailable');
    }
  }

  return (
    <div className="portal-page">
      <WorkPage title={partner.displayName} description={`Your figures for ${periodLabel(earnings.period)}.`}>
        {/* An invitation, not a demand: `mfa.required` is what a mandate looks
          like, and it ships off. This appears only where a partner could
          protect their account and has not. */}
        {mfa && mfa.enabled && !mfa.enrolled && !mfa.required ? (
          <div className="mb-4">
            <Alert tone="info" title="A second sign-in code is optional">
              You can sign in with your password alone. Adding an authenticator app or a security key is also what lets
              you change your payout address.{' '}
              <Link className="font-semibold text-[color:var(--color-brand)] underline" to="/partner/security">
                Set one up
              </Link>
              .
            </Alert>
          </div>
        ) : null}

        <WorkSection title="Your referral link">
          <p className="mt-1 break-all font-mono text-sm">{partner.url}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button onClick={() => void copyLink()}>Copy link</Button>
            <Link className="btn btn-secondary" to="/partner/account">
              Change link
            </Link>
            {copied ? (
              <span className="text-sm text-[color:var(--color-muted)]" role="status">
                {copied === 'yes' ? 'Copied.' : 'This browser will not let the page copy — select the link above.'}
              </span>
            ) : null}
          </div>
        </WorkSection>

        <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Commission rate"
            value={`${tier.currentRatePercent}%`}
            hint={
              tier.negotiated
                ? 'A negotiated rate, so the customer tiers do not apply.'
                : tier.nextRatePercent !== null && tier.customersToNextTier !== null
                  ? `${tier.customersToNextTier} more paying customers takes you to ${tier.nextRatePercent}%.`
                  : tier.maximumRatePercent !== null
                    ? `The top tier is ${tier.maximumRatePercent}%.`
                    : undefined
            }
          />
          <Stat
            label="Customers"
            value={customers.total}
            hint={`${customers.activePaying} paying · ${customers.notYetPaying} on a free plan or trial`}
          />
          <Stat
            label="This month, estimated"
            value={cash(earnings.estimatedCommissionCents)}
            // Said plainly rather than in a footnote: the month is not closed,
            // and a refund next week reverses part of it — which is why this is
            // the net of every movement in the period, the same figure the
            // Commissions screen totals.
            hint={`Net, on ${cash(earnings.eligibleRevenueCents)} of eligible revenue. Not final.`}
          />
          <Stat
            label="Payable balance"
            value={cash(earnings.payableBalanceCents)}
            hint={
              earnings.payableBalanceCents < earnings.minimumPayoutCents
                ? `Payouts start at ${cash(earnings.minimumPayoutCents)}; the rest carries forward.`
                : `Next payout run: day ${earnings.nextPayoutDay} of the month.`
            }
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Stat label="Lifetime commission" value={cash(earnings.lifetimeCommissionCents)} />
          <Stat label="Lifetime paid out" value={cash(earnings.lifetimePaidCents)} />
        </div>

        {/* Only what actually holds a payout. `calculatePayout` checks the tax
          paperwork only where `affiliate_require_tax_documentation` is on, so
          warning about it unconditionally told partners on a deployment that
          does not collect W-9s that their money was stuck when it was not. */}
        {!partner.paypalEmailOnFile || (partner.taxDocumentationRequired && !partner.taxDocumentationOnFile) ? (
          <div className="mt-4">
            <Alert tone="warning" title="Your payouts are not ready to send">
              {!partner.paypalEmailOnFile ? 'There is no PayPal address on file. ' : ''}
              {partner.taxDocumentationRequired && !partner.taxDocumentationOnFile
                ? 'Your tax documentation has not been received. '
                : ''}
              <Link className="font-semibold text-[color:var(--color-brand)] underline" to="/partner/account">
                Open your account details
              </Link>
              .
            </Alert>
          </div>
        ) : null}
      </WorkPage>
    </div>
  );
}
